import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import { Map, OrderedMap, fromJS } from 'immutable';
import CuesheetActions from 'flux/actions/cuesheet/CuesheetActions';
import EventViewActions from 'flux/actions/EventViewActions';
import TeamViewActions from 'flux/actions/TeamViewActions';
import GlobalElementActions from 'flux/actions/GlobalElementActions';
import CuesheetDataHelper from 'flux/stores/cuesheet/CuesheetDataHelper';
import AlertTypes from 'shared/constants/AlertTypes';
import AlertActions from 'flux/actions/AlertActions';
import ModalActions from 'flux/actions/ModalActions';
import Messages from 'shared/constants/Messages';
import UIRegions from 'shared/constants/UIRegions';
import { EditableCellTypes } from 'flux/stores/cuesheet/CuesheetConstants';
import qs from 'query-string';

export default Reflux.createStore({
  mixins: [BaseMixin],

  init() {
    const { titleRowsEnabled } = qs.parse(window.location.search, {
      arrayFormat: 'index',
    });

    this.setState(
      Map({
        titleRowsEnabled:
          !titleRowsEnabled || titleRowsEnabled === 'true' ? true : false,
      }),
    );

    this.listenTo(
      GlobalElementActions.populateGlobalElementTags.completed,
      'onPopulateGlobalElementTagsCompleted',
    );

    //Guest Pass
    this.listenTo(
      EventViewActions.populateForGuest.failed,
      'onEventPopulateFailed',
    );
    this.listenTo(
      EventViewActions.populateForGuest.completed,
      'onEventPopulateCompleted',
    );
    this.listenTo(
      TeamViewActions.populateForGuest.completed,
      'onTeamPopulateCompleted',
    );
    this.listenTo(CuesheetActions.populateForGuest, 'onCuesheetPopulate');
    this.listenTo(
      CuesheetActions.populateForGuest.completed,
      'onCuesheetPopulateCompleted',
    );
    this.listenTo(
      CuesheetActions.populateForGuest.failed,
      'onCuesheetPopulateFailed',
    );
  },

  onEventPopulateFailed(err) {
    /**
     * If event fails to populate with a 404 it is likely a deleted event.
     * Throw modal error for deleted events.
     */
    if (err.status === 404) {
      ModalActions.show(UIRegions.ViewingDeletedEventError);
    }
  },

  onEventPopulateCompleted(event) {
    this.setInStateAndTrigger('event', Map(event));

    if (event.globalElementManager) {
      GlobalElementActions.populateGlobalElementTags(event.team, event._id);
    }
  },

  onPopulateGlobalElementTagsCompleted(tags) {
    let allTags = OrderedMap();

    tags.forEach((tag) => {
      if (tag.deleted) return false;
      allTags = allTags.set(tag._id, fromJS(tag));
    });

    this.setInStateAndTrigger('tags', allTags);
  },

  onTeamPopulateCompleted(team) {
    this.setInStateAndTrigger('team', Map(team));
  },

  onCuesheetPopulate(teamId, eventId, showId) {
    this.setInState('teamId', teamId);
    this.setInState('eventId', eventId);
    this.setInState('showId', showId);
  },

  onCuesheetPopulateFailed(err) {
    if (err.status === 404)
      AlertActions.show(
        Messages.guestPassDisabled,
        null,
        AlertTypes.nondismissableAlert,
      );
    if (err.status === 403) {
      this.setInStateAndTrigger('unauthorized', true);
    }
  },

  onCuesheetPopulateCompleted(result) {
    const { itemSetList, show, globalElementShowItems } = result;

    if (show.archived) {
      return AlertActions.show(
        Messages.guestPassDisabled,
        null,
        AlertTypes.nondismissableAlert,
      );
    }

    if (this.getInState('unauthorized')) {
      this.setInState('unauthorized', false);
    }

    this._populateShowData(show);

    this._populateItemSetData(itemSetList, globalElementShowItems);
  },

  _populateShowData(show) {
    this.setInStateAndTrigger('show', Map(show));
  },

  _populateItemSetData(itemSetList, globalElementShowItems) {
    //TODO: we need to try to consolidate this w. a helper or mixin...this is basically the same as in CuesheetStore

    const userId = this.getInState('userId');

    const itemSet = Object.assign({}, itemSetList.itemSets[0]);

    const { rowOrder } = itemSet;

    this.setInState('itemSetListId', itemSetList._id);
    this.setInState('itemSetId', itemSet._id);

    const settings =
      Object.keys(itemSetList.settings).length > 0
        ? fromJS(itemSetList.settings)
        : fromJS({ itemColumn: { width: 'narrow' } });

    const userSettings = fromJS(
      itemSetList.userSettings[userId] || { noteColumn: { width: 'narrow' } },
    );

    let next = this.getState()
      .set('userSettings', userSettings)
      .set('settings', settings);

    //TODO: clean this up...move it to the data helper
    next = next.withMutations((st) => {
      const rowMap = CuesheetDataHelper.createNestedMap(
        itemSetList,
        globalElementShowItems,
      );
      const columnOrderGuest =
        CuesheetDataHelper.getGuestColumnOrder(itemSetList);

      //set the row index data
      st.set('rows', CuesheetDataHelper.createRowIndex(itemSetList, rowMap));
      //set the row map data
      st.set('rowMap', rowMap);
      st.set('rowOrder', rowOrder);

      //set the column data
      this._setGuestColumnState(columnOrderGuest, itemSetList.columns, st);

      return st;
    });

    next = CuesheetDataHelper.getStateWithDerivedCalculations(
      next,
      this.getInState(['show', 'autoTimeCalculation']),
      this.getInState(['show', 'startOn']),
    );

    this.setStateAndTrigger(next);
  },

  _getSpecialColumns(state) {
    state = state || this.getState();

    return CuesheetDataHelper.getSpecialColumnsForGuestAndPrintable(state);
  },

  /**
   * Set Guest Pass Column Order
   *
   * @param columnOrder //Default column order
   * @param columns //List of column objects for show
   * @param state
   *
   */
  _setGuestColumnState(columnOrder, columns, state) {
    state = state || this.getState();

    const dataColumns = CuesheetDataHelper.getSortedColumns(
      columns,
      columnOrder,
    );
    const allColumns = this._getSpecialColumns(state).concat(dataColumns);

    return state.set(
      'guestColumns',
      allColumns.delete(EditableCellTypes.ItemNote),
    );
  },
});
